/**
 * ShiftIdleCloseEditor — how long a driver's shift can sit idle before it is closed
 * automatically (OPEN-110, Corbin).
 *
 * Reads / saves GET / PATCH /v1/tenants/shift-settings (MANAGE_TENANT). No clock event, job
 * event or GPS point for this many hours closes the shift, backdated to the driver's last
 * activity rather than the time the check ran. Whole hours 4–16; 8 by default.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';

import { IDLE_CLOSE_HOURS_DEFAULT, parseIdleHours } from './idle-hours';

interface ShiftSettings {
  readonly shiftIdleCloseHours: number;
}

const help: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function ShiftIdleCloseEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const [saved, setSaved] = React.useState<ShiftSettings | null>(null);
  const [text, setText] = React.useState('');
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async (): Promise<void> => {
    try {
      const res = await authedFetch(opts, '/v1/tenants/shift-settings');
      if (!res.ok) {
        setLoadError(`Could not load the idle shift setting (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: ShiftSettings };
      setSaved(json.settings);
      setText(String(json.settings.shiftIdleCloseHours));
      setLoadError(null);
    } catch {
      setLoadError('Network error — please try again.');
    }
  }, [opts]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loadError !== null) {
    return (
      <div style={{ color: 'var(--color-danger)' }}>
        <p style={{ marginBottom: '8px' }}>{loadError}</p>
        <Button variant="outline" onClick={(): void => void load()}>
          Retry
        </Button>
      </div>
    );
  }
  if (saved === null) {
    return (
      <p role="status" style={help}>
        Loading…
      </p>
    );
  }

  const parsed = parseIdleHours(text);
  const unchanged = parsed.ok && parsed.hours === saved.shiftIdleCloseHours;

  const save = async (): Promise<void> => {
    if (!parsed.ok) return;
    setSaving(true);
    try {
      const res = await authedFetch(opts, '/v1/tenants/shift-settings', {
        method: 'PATCH',
        body: JSON.stringify({ shiftIdleCloseHours: parsed.hours }),
      });
      if (!res.ok) {
        toast.error(`Save failed (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: ShiftSettings };
      setSaved(json.settings);
      setText(String(json.settings.shiftIdleCloseHours));
      toast.success('Idle shift setting saved');
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '520px' }}>
      <label
        htmlFor="shift-idle-hours"
        style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}
      >
        Hours with no activity
      </label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Input
          id="shift-idle-hours"
          inputMode="numeric"
          value={text}
          aria-invalid={!parsed.ok}
          aria-describedby="shift-idle-hours-help"
          onChange={(e): void => setText(e.target.value)}
          style={{ maxWidth: '120px' }}
        />
        <Button
          type="button"
          disabled={!parsed.ok || unchanged || saving}
          onClick={(): void => void save()}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <p id="shift-idle-hours-help" style={{ ...help, marginTop: '6px' }}>
        {parsed.ok
          ? `A driver's shift is closed after ${String(parsed.hours)} hours with no clock, job or GPS activity.`
          : parsed.message}
      </p>
      <p style={{ ...help, marginTop: '10px' }}>
        The shift is closed at the driver&apos;s last recorded activity, not at the time the check
        runs, so their hours are not inflated. The default is {String(IDLE_CLOSE_HOURS_DEFAULT)}{' '}
        hours.
      </p>
    </div>
  );
}
